import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  alpha,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sessionManager } from "../context/sessionManager";
import TimerIcon from '@mui/icons-material/Timer';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ProfileIcon from "@mui/icons-material/AccountCircle";
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import { WorkspacePremium } from "@mui/icons-material";

const SessionIndicator = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [sessionTime, setSessionTime] = useState("3h 0m");
  const [userData, setUserData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);

  const open = Boolean(anchorEl);

  const colors = {
    light: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      secondary: theme.palette.secondary.main,
      secondaryLight: theme.palette.secondary.light,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(30, 41, 59, 0.3)',
      border: isDarkMode
        ? 'rgba(148, 163, 184, 0.1)'
        : 'rgba(99, 102, 241, 0.2)',
      chip: {
        background: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.3)',
        text: '#2563EB'
      }
    },
    dark: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      secondary: theme.palette.secondary.main,
      secondaryLight: theme.palette.secondary.light,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: theme.palette.mode === 'dark'
        ? 'rgba(30, 41, 59, 0.7)'
        : 'rgba(255, 255, 255, 0.92)',
      border: isDarkMode
        ? 'rgba(148, 163, 184, 0.1)'
        : 'rgba(99, 102, 241, 0.2)',
      chip: {
        background: 'rgba(96, 165, 250, 0.15)',
        border: 'rgba(96, 165, 250, 0.3)',
        text: '#60A5FA'
      }
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  const handleAutoLogout = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    sessionManager.clearSession();
    localStorage.removeItem("appAuth");
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const user = sessionManager.getUser();
    setUserData(user);

    const updateSessionTimer = () => {
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";

      setSessionTime(timeLeft);

      const sessionPercentage = calculatePercentageFromTime(timeLeft);
      setShowWarning(sessionPercentage < 20);

      if (timeLeft === 'Expired' || sessionPercentage <= 0) {
        handleAutoLogout();
      }
    };

    updateSessionTimer();
    timerRef.current = setInterval(updateSessionTimer, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [handleAutoLogout]);

  const calculatePercentageFromTime = (timeStr) => {
    if (timeStr === 'Expired') return 0;

    try {
      const hoursMatch = timeStr.match(/(\d+)h/);
      const minutesMatch = timeStr.match(/(\d+)m/);

      let totalMinutes = 0;
      if (hoursMatch) totalMinutes += parseInt(hoursMatch[1]) * 60;
      if (minutesMatch) totalMinutes += parseInt(minutesMatch[1]);

      const sessionDuration = 180;
      return Math.round((totalMinutes / sessionDuration) * 100);
    } catch (error) {
      return 100;
    }
  };

  const handleProfile = () => {
    navigate("/Profile");
    handleMenuClose();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    sessionManager.clearSession();
    localStorage.removeItem("appAuth");
    navigate('/login');
  };

  const refreshSession = () => {
    if (sessionManager.refreshSession && sessionManager.refreshSession()) {
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";
      setSessionTime(timeLeft);
      setShowWarning(false);
      handleMenuClose();
    } else if (sessionManager.refreshSession) {
      sessionManager.refreshSession();
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";
      setSessionTime(timeLeft);
      setShowWarning(false);
      handleMenuClose();
    }
  };

  const getTimerColor = () => {
    const percentage = calculatePercentageFromTime(sessionTime);
    if (percentage < 10) return '#EF4444';
    if (percentage < 30) return '#F59E0B';
    return '#10B981';
  };

  const getTimeForDisplay = () => {
    if (sessionTime === 'Expired') return 'Expired';
    return sessionTime;
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1, sm: 2 },
      position: 'relative',
      flexShrink: 0, // Prevent shrinking
    }}>
      {/* Session Timer - Only show on desktop, hidden on mobile */}
      {!isMobile && (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Session expires in: {sessionTime}
              </Typography>
              {showWarning && (
                <Typography variant="caption" sx={{ color: '#F59E0B', display: 'block' }}>
                  Session expiring soon
                </Typography>
              )}
            </Box>
          }
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '6px 12px',
              borderRadius: '12px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(248, 250, 252, 0.9))',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': {
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 1))',
                borderColor: alpha(getTimerColor(), 0.4),
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(getTimerColor(), 0.1)}`,
              }
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              <TimerIcon
                sx={{
                  fontSize: 18,
                  color: getTimerColor(),
                  animation: showWarning ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.6 },
                    '100%': { opacity: 1 },
                  }
                }}
              />

              {showWarning && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                    border: `2px solid ${isDark ? '#0F172A' : '#FFFFFF'}`,
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    }
                  }}
                />
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: getTimerColor(),
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                minWidth: '45px',
                textAlign: 'center'
              }}
            >
              {getTimeForDisplay()}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* User Profile - Fixed width container to prevent shifting */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '6px 12px',
          borderRadius: '12px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(248, 250, 252, 0.9))',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
          transition: 'all 0.2s ease',
          flexShrink: 0,
          minWidth: { xs: 'auto', sm: 'auto' },
          '&:hover': {
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 1))',
            borderColor: alpha(isDark ? '#60A5FA' : '#3B82F6', 0.4),
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(isDark ? '#60A5FA' : '#3B82F6', 0.1)}`,
          }
        }}
      >
        {userData && (
          <>
            {/* Show name and admin on desktop, hide on mobile */}
            {!isMobile && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                mr: 1
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? '#E2E8F0' : '#1E293B',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userData.shopName || userData.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? '#94A3B8' : '#64748B',
                    fontSize: '0.7rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Admin
                </Typography>
              </Box>
            )}

            {/* Avatar with warning dot */}
            <Box sx={{ position: 'relative', display: 'flex' }}>
              <IconButton
                onClick={handleMenuClick}
                size="small"
                sx={{
                  color: isDark ? '#E2E8F0' : '#1E293B',
                  background: isDark
                    ? 'rgba(139, 92, 246, 0.1)'
                    : 'rgba(139, 92, 246, 0.08)',
                  border: `1px solid ${alpha('#8B5CF6', 0.2)}`,
                  padding: '4px',
                  '&:hover': {
                    background: isDark
                      ? 'rgba(139, 92, 246, 0.2)'
                      : 'rgba(139, 92, 246, 0.15)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {userData.avatar ? (
                  <Avatar
                    src={userData.avatar}
                    sx={{ width: 28, height: 28 }}
                  />
                ) : (
                  <AccountCircleIcon sx={{ fontSize: 28 }} />
                )}
              </IconButton>

              {/* Warning dot on avatar for mobile when session expiring */}
              {showWarning && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                    border: `2px solid ${isDark ? '#1E293B' : '#FFFFFF'}`,
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    }
                  }}
                />
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Menu - No changes here */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: '12px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.98))',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            minWidth: 260,
          }
        }}
      >
        {userData && (
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
                  border: `2px solid ${alpha('#8B5CF6', 0.3)}`,
                }}
              >
                {userData.name?.charAt(0) || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                  {userData.shopName || userData.name}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                  {userData.email}
                </Typography>
              </Box>
            </Box>

            <Chip
              label="Administrator"
              size="small"
              icon={<SecurityIcon sx={{ fontSize: 14 }} />}
              sx={{
                mt: 1,
                background: alpha('#10B981', 0.1),
                color: '#10B981',
                border: `1px solid ${alpha('#10B981', 0.2)}`,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Box>
        )}

        <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

        {/* Session Status in Menu */}
        <Box sx={{ p: 2, pb: 1, bgcolor: alpha(getTimerColor(), 0.05) }}>
          <Typography variant="caption" sx={{
            color: isDark ? '#94A3B8' : '#64748B',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 1,
            display: 'block'
          }}>
            Session Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ fontSize: 16, color: getTimerColor() }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: getTimerColor() }}>
                {sessionTime} remaining
              </Typography>
            </Box>
            {showWarning && (
              <Chip
                label="Expiring Soon"
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: '0.6rem' }}
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

        {/* Refresh Session Button */}
        <MenuItem
          onClick={refreshSession}
          sx={{
            py: 1.5,
            px: 2,
            m: 1,
            borderRadius: 2,
            '&:hover': {
              background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            }
          }}
        >
          <RefreshIcon sx={{ mr: 2, fontSize: 20, color: '#3B82F6' }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Refresh Session
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              Extend for 3 more hours
            </Typography>
          </Box>
        </MenuItem>

        <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

        <MenuItem
          onClick={handleProfile}
          sx={{
            py: 1.2,
            px: 2,
            m: 1,
            borderRadius: 2,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(96, 165, 250, 0.15)"
                : "rgba(37, 99, 235, 0.1)",
            },
          }}
        >
          <ProfileIcon sx={{ mr: 1.5, fontSize: 22, color: currentColors.primary }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: currentColors.textPrimary }}>
            My Profile
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate('/SubscriptionPage');
          }}
          sx={{
            py: 1.2,
            px: 2,
            m: 1,
            borderRadius: 2,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(96, 165, 250, 0.15)"
                : "rgba(37, 99, 235, 0.1)",
            },
          }}
        >
          <WorkspacePremium sx={{ mr: 1.5, fontSize: 22, color: currentColors.primary }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: currentColors.textPrimary }}>
            Subscription Plans
          </Typography>
        </MenuItem>

        <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            px: 2,
            m: 1,
            borderRadius: 2,
            '&:hover': {
              background: alpha('#EF4444', 0.1),
            }
          }}
        >
          <LogoutIcon sx={{ mr: 2, fontSize: 20, color: '#EF4444' }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#EF4444' }}>
              Logout
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              End current session
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SessionIndicator;